import {getTestProps} from '@benchling/js-test-utils';
import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSidepanelContext,
  NodeDetailsSidepanelContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import FlowchartContextPanel, {
  ContextPanelContent,
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {
  NodeDetailsSidepanelContentBaseProps,
  SidepanelTab,
  TaskNodeDetailsSidepanelContentProps,
} from './processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveRouterNode,
  isInteractiveTaskNode,
  isPlaceholderForAddExistingTaskSchemaData,
  isPlaceholderForTaskSchemaCreateData,
} from './processFlowchartConfigEditorNodeTypes';
import {PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE} from './processFlowchartConfigEditorRegistry';
import {LiveStateChanges} from './redux/FlowchartConfigStateManager';
import {FlowchartSidepanelUnsavedChangesContext} from './right-sidepanel/FlowchartSidepanelUnsavedChangesContext';
import PlaceholderNodeDetailsSidepanelContent from './right-sidepanel/PlaceholderNodeDetailsSidepanelContent';
import RouterNodeDetailsSidepanelContent from './right-sidepanel/RouterNodeDetailsSidepanelContent';
import TaskNodeDetailsSidepanelContent from './right-sidepanel/TaskNodeDetailsSidepanelContent';
import {useFlowchartSidepanelUnsavedChanges} from './right-sidepanel/useFlowchartSidepanelUnsavedChanges';

type RightSidepanelProps = NodeDetailsSidepanelContentBaseProps &
  Omit<NodeDetailsSidepanelContextValue, 'selectedNode'> & {
    selectedNode: FlowchartNode | null;
    selectedFieldId: string | null;
    isOpen: boolean;
    platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>;
    onClose: () => void;
    size: SidepanelSize;
    titleOverride?: string;
    liveStateChanges: LiveStateChanges;
    onRevertNodeSelection?: (nodeId: string, fieldId: string | null) => void;
    nodesBeingDeletedRef?: React.MutableRefObject<Set<string> | null>;
    rightSidepanelRef?: React.RefObject<HTMLDivElement>;
  };

function getSidepanelTabsForPlatformFeatures(
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>
): Array<SidepanelTab> {
  if (platformFlowchartFeatures == null) {
    return [];
  }
  const platformFeatureSidepanelTabs = [];
  for (const [feature, enabled] of platformFlowchartFeatures) {
    if (enabled && PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature] != null) {
      const tabs = PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature];
      platformFeatureSidepanelTabs.push(...tabs);
    }
  }
  return platformFeatureSidepanelTabs;
}

function RightSidepanelContent(props: RightSidepanelProps & {selectedNode: FlowchartNode}): JSX.Element {
  const {liveStateChanges} = props;
  const [isAutosaverPaused, setIsAutosaverPaused] = liveStateChanges.isAutosaverPausedState;
  const [isAutosaverBlocked, _setIsAutosaverBlocked] = liveStateChanges.isAutosaverBlockedState;
  const [isSaveInFlight, _setIsSaveInFlight] = liveStateChanges.isSaveInFlightState;
  const [isDirty, _setIsDirty] = liveStateChanges.dirtyState;
  const [isReadonly, _setIsReadonly] = liveStateChanges.readonlyState;

  if (isInteractiveTaskNode(props.selectedNode)) {
    const taskNodeData = props.selectedNode.data;

    const nodeDetailsSidepanelContentProps: TaskNodeDetailsSidepanelContentProps = {
      selectedNodeTaskDefinitionVersionSpec: taskNodeData.taskDefinitionVersionSpec,
      selectedNodeId: props.selectedNode.id,
      flowchart: props.flowchart,
      selectedFieldId: props.selectedFieldId,
      locked: props.locked,
      onUpdateTaskDefinitionVersion: props.onUpdateTaskDefinitionVersion,
      onChangeSelectedFieldId: props.onChangeSelectedFieldId,
      validationErrors: props.validationErrors,
      isAutosaverPaused,
      setIsAutosaverPaused,
      isAutosaverBlocked,
      isSaveInFlight,
      isDirty,
      isReadonly,
    };

    const tabs = [
      ...taskNodeData.nodeDetailsSidepanelContent,
      ...getSidepanelTabsForPlatformFeatures(props.platformFlowchartFeatures),
    ];

    return (
      <TaskNodeDetailsSidepanelContent
        nodeDetailsSidepanelContentProps={nodeDetailsSidepanelContentProps}
        tabs={tabs}
      />
    );
  } else if (isInteractivePlaceholderNode(props.selectedNode)) {
    return (
      <ContextPanelContent>
        <PlaceholderNodeDetailsSidepanelContent selectedNode={props.selectedNode} />
      </ContextPanelContent>
    );
  } else if (isInteractiveRouterNode(props.selectedNode)) {
    return <RouterNodeDetailsSidepanelContent selectedNode={props.selectedNode} />;
  } else {
    assert(
      false,
      `This node type is not supported in the right sidepanel: ${props.selectedNode.data.nodeName}`
    );
  }
}

// TODO(BNCH-126721): Consolidate with transition modal right sidepanel
export default function ProcessFlowchartConfigEditorRightSidepanel(
  props: RightSidepanelProps
): JSX.Element | null {
  const {
    selectedNode,
    selectedFieldId,
    isOpen,
    onClose,
    onRevertNodeSelection,
    rightSidepanelRef,
    ...contextProps
  } = props;

  const {
    registerTab,
    unregisterTab,
    setIsTabDirty,
    handleClose: handleCloseWithUnsavedChanges,
    getEffectiveState,
  } = useFlowchartSidepanelUnsavedChanges(
    onClose,
    selectedNode,
    selectedFieldId,
    props.titleOverride,
    onRevertNodeSelection,
    props.nodesBeingDeletedRef
  );

  // Use the effective state to prevent content changes while modal is open
  const {
    node: effectiveSelectedNode,
    fieldId: effectiveSelectedFieldId,
    titleOverride: effectiveTitleOverride,
  } = getEffectiveState();

  const nodeDetailsContextValue = useMemo((): NodeDetailsSidepanelContextValue | null => {
    if (!effectiveSelectedNode) {
      return null;
    }

    return {
      ...contextProps,
      selectedNode: effectiveSelectedNode,
    };
  }, [contextProps, effectiveSelectedNode]);

  const unsavedChangesContextValue = useMemo(
    () => ({
      registerTab,
      unregisterTab,
      setIsTabDirty,
    }),
    [registerTab, unregisterTab, setIsTabDirty]
  );

  const getSidepanelTitle = useCallback(() => {
    if (effectiveSelectedNode == null) {
      return 'Node details';
    } else if (isInteractivePlaceholderNode(effectiveSelectedNode)) {
      if (isPlaceholderForAddExistingTaskSchemaData(effectiveSelectedNode.data.placeholderForNodeTypeData)) {
        return 'Add existing task schema';
      } else if (
        isPlaceholderForTaskSchemaCreateData(effectiveSelectedNode.data.placeholderForNodeTypeData)
      ) {
        return 'Create new task schema';
      } else {
        throw new Error('Unexpected placeholder type');
      }
    } else {
      return `Node ${effectiveSelectedNode.data.nodeName} Details`;
    }
  }, [effectiveSelectedNode]);

  const effectiveSidepanelKey = useMemo(() => effectiveSelectedNode?.id ?? 'right', [effectiveSelectedNode]);

  return (
    <FlowchartContextPanel
      panelSide={PanelSide.RIGHT}
      sidepanelName={effectiveTitleOverride ?? getSidepanelTitle()}
      sidepanelIcon="settings"
      isOpen={isOpen && effectiveSelectedNode != null}
      onClose={handleCloseWithUnsavedChanges}
      size={props.size}
      sidepanelKey="right"
    >
      {nodeDetailsContextValue && effectiveSelectedNode && (
        <NodeDetailsSidepanelContext.Provider key={effectiveSidepanelKey} value={nodeDetailsContextValue}>
          <FlowchartSidepanelUnsavedChangesContext.Provider value={unsavedChangesContextValue}>
            <div
              ref={rightSidepanelRef}
              {...getTestProps({component: 'ProcessFlowchartConfigEditorRightSidepanel', element: 'root'})}
            >
              <RightSidepanelContent
                {...props}
                selectedNode={effectiveSelectedNode}
                selectedFieldId={effectiveSelectedFieldId}
              />
            </div>
          </FlowchartSidepanelUnsavedChangesContext.Provider>
        </NodeDetailsSidepanelContext.Provider>
      )}
    </FlowchartContextPanel>
  );
}
